import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import useAuthStore from "../../../store/authStore";
import settingsIcon from "../../../assets/images/SBA/settings_icons.png";
import excelIcon from "../../../assets/images/SBA/excel_icon.png";
import pdfIcon from "../../../assets/images/SBA/pdf_icon.png";
import downloadIcon from "../../../assets/images/SBA/img_Download.png";
import newIcon from "../../../assets/images/SBA/New-icon.png";

import {
  getEntitiesByInvestor,
  getInvestorsByContact,
  getStatementsByFilters,
  downloadDocumentBlob,
  openBlobInNewTab,
  saveBlob,
  getClientDocumentsSinglePDFDownloadUrl,
  getDocumentAsSingleZipDownloadUrl,
  getStatementCustomize,
  saveStatementCustomize,
  getStatementZipDownloadUrl,
  downloadStatementZipBlob 
} from "../services/assetStatementsService";
import "../styles/assetStatements.css";
import { toast } from "react-toastify";
import { exportToExcel } from "../../../shared/utils/exportToExcel";
import { AESClientWebsiteDrawer } from "../../../shared/utils/aesClientWebsiteDrawer";
import { downloadZipByUrl } from "../../../shared/utils/downloadZip";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import ToolbarSelect from "./ToolbarSelect";

ModuleRegistry.registerModules([AllCommunityModule]);
const getAlignmentStyle = (params) => {
  const value = params?.value;

  // actual numbers
  if (typeof value === "number") {
    return { textAlign: "right" };
  }

  // numeric strings like:
  // "25%", "-10.5", "1,245.88", etc.
  if (typeof value === "string") {
    const numericValue = Number(
      value
        .replace("%", "")
        .replace(/,/g, "")
        .trim()
    );

    const isValidNumber = !isNaN(numericValue);

    if (isValidNumber) {
      return { textAlign: "right" };
    }

    return { textAlign: "left" };
  }

  return null;
};
const get = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal];

const toInputDate = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toToolbarOptions = (items = [], valueKeyCamel, valueKeyPascal, labelKeyCamel, labelKeyPascal) =>
  items.map((item) => ({
    value: get(item, valueKeyCamel, valueKeyPascal),
    label: get(item, labelKeyCamel, labelKeyPascal),
  }));

const mapLabelToField = (name) => {
  const n = (name || "").trim().toLowerCase();
  if (n === "investor name") return "InvestorName";
  if (n === "document type") return "DocumentTypeName";
  if (n === "entity" || n === "entity name") return "EntityName";
  if (n === "document name") return "DocumentName";
  if (n === "date" || n === "document date") return "DocumentDate";
  return null;
};

const AssetStatements = () => {
  useDocumentTitle("Asset Statements");

  const { contactId: contactIdParam } = useParams();
  const contactId = Number(atob(contactIdParam));

  const user = useAuthStore((s) => s) || {};
  const userId = get(user, "user", "UserId");
  const appRoleId = get(user, "role", "AppRoleId");

  const [isLoading, setIsLoading] = useState(true);

  const [investors, setInvestors] = useState([]);
  const [entities, setEntities] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedInvestor, setSelectedInvestor] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    return toInputDate(new Date(now.getFullYear() - 1, 0, 31));
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return toInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 1));
  });

  const [description, setDescription] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [pageSize, setPageSize] = useState(15);
  const [gridApi, setGridApi] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  const [customizeRows, setCustomizeRows] = useState([]);
  const [gridQueryId, setGridQueryId] = useState(0);
  const [visibleCols, setVisibleCols] = useState(new Set());

  const selectedEntityObj = useMemo(
    () =>
      entities.find(
        (e) => String(get(e, "entityId", "EntityId")) === String(selectedEntity)
      ),
    [entities, selectedEntity]
  );
  const investorOptions = useMemo(
    () => toToolbarOptions(investors, "investorId", "InvestorId", "investorName", "InvestorName"),
    [investors]
  );
  const entityOptions = useMemo(
    () => toToolbarOptions(entities, "entityId", "EntityId", "entityName", "EntityName"),
    [entities]
  );


  const entityTypeId = Number(selectedEntity) === -1
  ? null
  : Number(get(selectedEntityObj || {}, "entityTypeId", "EntityTypeId") || 0);


  const getCustomizeUserName = () => {
    const first =
      get(user, "inputUserName", "displayUserName") ||
      "User";
    return `Default_${first}`;
  };

  const applyClientFilters = (list, fFromDate, fToDate, fDescription) => {
    const from = fFromDate ? new Date(`${fFromDate}T00:00:00`) : null;
    const to = fToDate ? new Date(`${fToDate}T23:59:59`) : null;
    const q = (fDescription || "").trim().toLowerCase();

    return (list || []).filter((r) => {
      const name = (get(r, "documentName", "DocumentName") || "").toLowerCase();
      const dtRaw = get(r, "documentDate", "DocumentDate");
      const dt = dtRaw ? new Date(dtRaw) : null;

      const dateOk = (!from || (dt && dt >= from)) && (!to || (dt && dt <= to));
      const nameOk = !q || name.includes(q);
      return dateOk && nameOk;
    });
  };

const fetchGrid = async (override = {}) => {
  if (!selectedInvestor || !selectedEntity) return;

  setIsLoading(true);
  try {
    const investorVal = override.investorId ?? selectedInvestor;
    const entityVal = override.entityId ?? selectedEntity;
    const entityTypeVal = override.entityTypeId ?? entityTypeId;

    const invId = Number(investorVal) === -1 ? null : Number(investorVal);
    const entId = Number(entityVal) === -1 ? null : Number(entityVal);
    const eType = Number(entityVal) === -1 ? null : Number(entityTypeVal || 0);

    const res = await getStatementsByFilters({
      investorId: invId,
      entityId: entId,
      entityTypeId: eType,
      documentGroup: "1",
      contactId,
      userId,
      AppRoleId: appRoleId,
      fromDate: "",
      toDate: "",
      description: "",
    });

    const raw = res?.data.filter(d => get(d, "documentTypeId", "DocumentTypeId") == 5);
    const fullList = Array.isArray(raw) ? raw : raw?.data || [];

    const filteredList = applyClientFilters(
      fullList,
      override.fromDate ?? fromDate,
      override.toDate ?? toDate,
      override.description ?? description
    );

    setRows(filteredList);
    setTotalRecords(fullList.length);
    setFilteredRecords(filteredList.length);
    setSelectedDocIds(new Set());
  } finally {
    setIsLoading(false);
  }
};

const loadEntities = async (investorId) => {
  const normalizedInvestorId = Number(investorId) === -1 ? null : Number(investorId);
  const entRes = await getEntitiesByInvestor(normalizedInvestorId, contactId);
  const raw = entRes?.data || [];

  const allOption = {
    entityId: -1,
    entityName: "--All--",
    entityTypeId: null,
    EntityId: -1,
    EntityName: "--All--",
    EntityTypeId: null,
  };

  const list = raw.length > 1 ? [allOption, ...raw] : raw;
  setEntities(list);

  if (!list.length) {
    setSelectedEntity("");
    setRows([]);
    setTotalRecords(0);
    setFilteredRecords(0);
    setIsLoading(false);
    return;
  }

  if (list.length === 1) {
    const onlyId = get(list[0], "entityId", "EntityId");
    setSelectedEntity(String(onlyId));
  } else {
    setSelectedEntity("-1");
  }
};


useEffect(() => {
  if (!contactId || Number.isNaN(contactId)) return;

  const init = async () => {
    setIsLoading(true);

    const invRes = await getInvestorsByContact(contactId);
    const raw = invRes?.data || [];

    const allInvestor = {
      investorId: -1,
      investorName: "--All--",
      InvestorId: -1,
      InvestorName: "--All--",
    };

    const invList = raw.length > 1 ? [allInvestor, ...raw] : raw;
    setInvestors(invList);

    if (!invList.length) {
      setRows([]);
      setIsLoading(false);
      return;
    }

    if (invList.length === 1) {
      const onlyId = get(invList[0], "investorId", "InvestorId");
      setSelectedInvestor(String(onlyId));
      await loadEntities(onlyId);
    } else {
      setSelectedInvestor("-1");
      await loadEntities(-1);
    }
  };

  init();
}, [contactId]);

  useEffect(() => {
    if (selectedInvestor && selectedEntity) {
      fetchGrid();
    }
  }, [selectedInvestor, selectedEntity, entityTypeId]);

  const onInvestorChange = async (e) => {
    const investorId = e.target.value;
    setIsLoading(true);
    setSelectedInvestor(investorId);
    await loadEntities(investorId);
  };

  const onEntityChange = (e) => {
    setSelectedEntity(e.target.value);
  };

  const toggleDoc = (docId) => {
    const key = String(docId);
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const downloadSingleDoc = async (row, openInlinePdf = false) => {
    const docId = get(row, "documentId", "DocumentId");
    const docName = get(row, "documentName", "DocumentName") || `Document_${docId}.pdf`;
    const extension = (docName.split(".").pop() || "").toLowerCase();

    const res = await downloadDocumentBlob(docId, false, userId, appRoleId);
    const blob = res?.data;
    if (!blob) return;

    if (openInlinePdf && extension === "pdf") {
      openBlobInNewTab(blob);
      return;
    }

    saveBlob(blob, docName);
  };

  const downloadRow = async (row) => {
    const docName = get(row, "documentName", "DocumentName") || "";
    const extension = (docName.split(".").pop() || "").toLowerCase();
    await downloadSingleDoc(row, extension === "pdf");
  };

  const onDownloadSelectedPdf = async () => {
    const selectedRows = rows.filter((r) =>
      selectedDocIds.has(String(get(r, "documentId", "DocumentId")))
    );

    if (!selectedRows.length) {
      toast.error("Please select at least one item to download.");
      return;
    }

    const pdfRows = selectedRows.filter((r) => {
      const nm = get(r, "documentName", "DocumentName") || "";
      return (nm.split(".").pop() || "").toLowerCase() === "pdf";
    });

    if (!pdfRows.length) {
      toast.error("Please select at least one PDF to download.");
      return;
    }

    for (const row of pdfRows) {
      await downloadSingleDoc(row, false);
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  };

  const formatZipFileName = (name) =>
    String(name || "Documents").replace(/[<>:"/\\|?*]+/g, "").trim();

  const formatZipTimestamp = (d = new Date()) => {
    const MM = String(d.getMonth() + 1).padStart(2, "0");
    const DD = String(d.getDate()).padStart(2, "0");
    const YYYY = d.getFullYear();
    const H = String(d.getHours());
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${MM}${DD}${YYYY}${H}${mm}${ss}`;
  };

  const onDownloadZip = async () => {
    const ids = rows
      .filter((r) => selectedDocIds.has(String(get(r, "documentId", "DocumentId"))))
      .map((r) => get(r, "documentId", "DocumentId"));

    if (!ids.length) {
      toast.error("Please select at least one item to download.");
      return;
    }

    const res = await downloadStatementZipBlob({
      documentIds: ids,
      fileName: "Statements",
      userId,
      appRoleId,
      isRejected: false,
    });

    const blob = res?.data;
    if (!blob) return;

    const investorObj = investors.find(
      (inv) =>
        String(get(inv, "investorId", "InvestorId")) === String(selectedInvestor)
    );

    const contactName = formatZipFileName(
      get(investorObj, "investorName", "InvestorName")
    );

    const fileName = `${contactName}_${formatZipTimestamp()}.zip`;

    saveBlob(blob, fileName);
  };

  const onGo = async () => {
    await fetchGrid();
  };

  const onExportExcel = () => {
    const headers = ["Investor Name", "Document Type", "Entity", "Document Name", "Date"];

    const exportRows = rows.map((r) => ({
      "Investor Name": get(r, "investorName", "InvestorName") || "",
      "Document Type": get(r, "documentTypeName", "DocumentTypeName") || "",
      Entity: get(r, "entityName", "EntityName") || "",
      "Document Name": get(r, "documentName", "DocumentName") || "",
      Date: get(r, "documentDate", "DocumentDate")
        ? new Date(get(r, "documentDate", "DocumentDate")).toLocaleDateString("en-US")
        : "",
    }));

    exportToExcel({
      fileName: "Statements.xlsx",
      headers,
      rows: exportRows,
    });
  };

  const openPreviewPage = async (row) => {
    const docId = get(row, "documentId", "DocumentId");
    const docName = get(row, "documentName", "DocumentName") || "";
    const extension = (docName.split(".").pop() || "").toLowerCase();

    if (extension !== "pdf") {
      downloadRow(row);
      return;
    }

    try {
      const res = await downloadDocumentBlob(docId, false, userId, appRoleId);
      const blob = res?.data;

      if (!blob) return;  
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error("Preview failed", e);
    }
  };

  const onOpenCustomize = async () => {
    const res = await getStatementCustomize(2, userId, getCustomizeUserName());
    const dto = res?.data || {};
    const cols = get(dto, "columnsSettings", "ColumnsSettings") || [];
    const qid = Number(get(dto, "gridQueryID", "GridQueryID") || 0);

    setGridQueryId(qid);
    const normalizedRows = cols.map((r, i) => ({
      columnName: get(r, "columnName", "ColumnName"),
      isVisible: !!get(r, "isVisible", "IsVisible"),
      order: Number(get(r, "order", "Order") ?? i),
    }));
    normalizedRows.sort((a, b) => a.order - b.order);
    setCustomizeRows(normalizedRows);

    const nextVisible = new Set();
    normalizedRows.forEach((r) => {
      if (r.isVisible) {
        const f = mapLabelToField(r.columnName);
        if (f) nextVisible.add(f);
      }
    });
    if (nextVisible.size) setVisibleCols(nextVisible);

    setShowCustomize(true);
  };

  const onToggleCustomizeAll = (checked) => {
    setCustomizeRows((prev) => prev.map((r) => ({ ...r, isVisible: checked })));
  };

  const onSaveCustomize = async () => {
    const payload = {
      ColumnsSettings: customizeRows.map((r, i) => ({
        ColumnName: r.columnName,
        IsVisible: !!r.isVisible,
        SortOrder: i,
        GroupIndex: gridQueryId,
      })),
      GridID: 123,
      GridQueryID: gridQueryId || 1234,
      IsDefault: true,
      IsShared: true,
      PageID: 2,
      QueryName: getCustomizeUserName(),
      UserID: userId,
    };

    await saveStatementCustomize(payload);
    await loadSavedCustomize();
    setShowCustomize(false);
  };

  const loadSavedCustomize = async () => {
    const res = await getStatementCustomize(2, userId, getCustomizeUserName());
    const dto = res?.data || {};

    const cols =
      get(dto, "columnsSettings", "ColumnsSettings") || [];

    setGridQueryId(get(dto, "gridQueryID", "GridQueryID") || 0);

    if (!cols.length) return;

    const nextVisible = new Set();

    cols.forEach((c) => {
      const visible = get(c, "isVisible", "IsVisible");

      if (visible) {
        const name = get(c, "columnName", "ColumnName");
        const f = mapLabelToField(name);
        if (f) nextVisible.add(f);
      }
    });

    if (nextVisible.size) setVisibleCols(nextVisible);

    setCustomizeRows(
      cols.map((c, i) => ({
        columnName: get(c, "columnName", "ColumnName"),
        isVisible: get(c, "isVisible", "IsVisible"),
        order: Number(get(c, "order", "Order") ?? i),
      }))
    );
  };

  useEffect(() => {
    if (!user) return;
    loadSavedCustomize();
  }, [user]);


  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      suppressHeaderFilterButton: true
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Investor Name",
        cellStyle:getAlignmentStyle,
        field: "InvestorName",
        flex: 1.2,
        minWidth: 170,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "investorName", "InvestorName") || "",
      },
      {
        headerName: "Document Type",
        cellStyle:getAlignmentStyle,
        field: "DocumentTypeName",
        flex: 1.1,
        minWidth: 160,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "documentTypeName", "DocumentTypeName") || "",
      },
      {
        headerName: "Entity",
        cellStyle:getAlignmentStyle,
        field: "EntityName",
        flex: 1.1,
        minWidth: 150,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "entityName", "EntityName") || "",
      },
      {
        headerName: "Document Name",
        cellStyle:getAlignmentStyle,
        field: "DocumentName",
        flex: 1.7,
        minWidth: 240,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "documentName", "DocumentName") || "",
        cellRenderer: (p) => {
          const name = p.value || "";
          const isNew = !!get(p.data, "isNew", "IsNew");
          const viewed = !!get(p.data, "viewed", "Viewed");
          return (
            <span>
              {name}
              {isNew && !viewed ? (
                <span>
                <img src={newIcon} alt="new" style={{ marginLeft: 6, width: 28, height: 18, cursor:"pointer" }} /></span>
              ) : null}
            </span>
          );
        },
      },
      {
        headerName: "Date",
        cellStyle:getAlignmentStyle,
        field: "DocumentDate",
        flex: 0.9,
        minWidth: 130,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "documentDate", "DocumentDate") || null,
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString("en-US") : ""),
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const d = new Date(cellValue);
            const cellDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            if (cellDate < filterLocalDateAtMidnight) return -1;
            if (cellDate > filterLocalDateAtMidnight) return 1;
            return 0;
          },
        },
      },
      {
        headerName: "Select",
        cellStyle:getAlignmentStyle,
        field: "Select",
        width: 90,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params) => {
          const docId = get(params.data, "documentId", "DocumentId");
          const key = String(docId);
          return (
            <input
              type="checkbox"
              checked={selectedDocIds.has(key)}
              onChange={() => toggleDoc(docId)}
            />
          );
        },
      },
    ],
    [selectedDocIds]
  );


  const visibleColumnDefs = useMemo(() => {
    if (!visibleCols.size) return columnDefs;
    return columnDefs.filter(
      (c) => c.field === "Select" || visibleCols.has(c.field)
    );
  }, [columnDefs, visibleCols]);

  const allChecked = customizeRows.length > 0 && customizeRows.every((r) => r.isVisible);

  const customizeColumnDefs = useMemo(
    () => [
      {
        headerName: "",
        cellStyle:getAlignmentStyle,
        field: "isVisible",
        width: 90,

        headerComponent: () => {
          const allChecked =
            customizeRows.length > 0 &&
            customizeRows.every((r) => r.isVisible);

          return (
            <input
              type="checkbox"
              checked={allChecked}
              onChange={(e) => {
                const checked = e.target.checked;

                setCustomizeRows((prev) =>
                  prev.map((row) => ({
                    ...row,
                    isVisible: checked,
                  }))
                );
              }}
            />
          );
        },

        cellRenderer: (params) => (
          <input
            type="checkbox"
            checked={params.value}
            onChange={(e) => {
              const checked = e.target.checked;

              setCustomizeRows((prev) =>
                prev.map((row, i) =>
                  i === params.node.rowIndex
                    ? { ...row, isVisible: checked }
                    : row
                )
              );
            }}
          />
        ),
      },
      {
        headerName: "Column Name",
        cellStyle:getAlignmentStyle,
        field: "columnName",
        flex: 1,
      },
    ],
    [customizeRows]
  );
  const onCustomizeRowDragEnd = (event) => {
    const updatedRows = [];

    event.api.forEachNode((node) => {
      updatedRows.push(node.data);
    });

    setCustomizeRows(updatedRows);
  };

  return (
    <div className="minHeightDiv1">
      <section className="col-md-12 action-bar">
        <form className="statement-filter-form" onSubmit={(e) => e.preventDefault()}>
          <div className="row statement-filter-row toolbar">
              <div className="col-sm-4 col-md-3 col-lg-2 statement-filter-item statement-filter-item-top">
                <label className="control-label custom-fonts-label filter-lbl" style={{ marginRight: "10px" }}>
                  Investor:
                </label>
                <ToolbarSelect
                  value={selectedInvestor}
                  onChange={(nextValue) => onInvestorChange({ target: { value: nextValue } })}
                  options={investorOptions}
                />
              </div>

              <div className="col-sm-4 col-md-3 col-lg-2 statement-filter-item statement-filter-item-top">
                <label className="control-label custom-fonts-label filter-lbl" style={{ marginRight: 10 }}>
                  Entity:
                </label>
                <ToolbarSelect
                  value={selectedEntity}
                  onChange={(nextValue) => onEntityChange({ target: { value: nextValue } })}
                  options={entityOptions}
                />
              </div>

              <div
                className="col-sm-4 col-md-3 col-lg-2 statement-filter-item statement-filter-item-top"
                style={{ position: "relative" }}
              >
                <label className="" style={{ marginRight: 10 }}>
                  From:
                </label>
                <input className="fromDate form-control custom-fonts" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>

              <div
                className="col-sm-4 col-md-3 col-lg-2 statement-filter-item statement-filter-item-top"
                style={{ position: "relative" }}
              >
                <label className="" style={{ marginRight:10 }}>
                  To:
                </label>
                <input className="toDate form-control custom-fonts" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>


            <div className="col-sm-4 col-md-4 col-lg-2 statement-filter-item statement-filter-item-bottom">
                <label className="control-label custom-fonts-label filter-lbl" style={{ marginRight: 10 }}>
                  Name:
                </label>
                <input
                  className="form-control custom-fonts"
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onGo()}
                  />
            </div>
            
            <div
              className="col-sm-4 col-md-4 col-lg-2 statement-filter-item statement-filter-item-bottom statement-go-col"
              style={{ justifyContent: "left" }}
            >
              <button type="button" className="btn btn2 focus-remove custom-fonts go-btn" style={{ marginTop: 0 }} onClick={onGo}>
                Go
              </button>
            </div>
          </div>
        </form>
      </section>

      <div className="clearfix" />
      <div className="row" style={{ height: 15 }} />

      <section className="col-md-12 asset-content-ov">
        <div className="statement-toolbar">
          <div className="statement-toolbar-right">
            <div className="statement-counts">
              <span>Total : {totalRecords}</span>
              <span>|</span>
              <span>Filtered : {filteredRecords}</span>
            </div>

            <button type="button" className="icon-btn" title="Customize" onClick={onOpenCustomize}>
              <img src={settingsIcon} alt="Customize" />
            </button>
            <button type="button" className="icon-btn" title="Export to Excel" onClick={onExportExcel}>
              <img src={excelIcon} alt="Excel" />
            </button>
            <button type="button" className="icon-btn" title="Download PDF" onClick={onDownloadSelectedPdf}>
              <img src={pdfIcon} alt="PDF" />
            </button>
            <button type="button" className="icon-btn" title="Download As Zip" onClick={onDownloadZip}>
              <img src={downloadIcon} alt="Zip" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="ag-theme-alpine" style={{ width: "100%" }}>
            <AgGridReact
              theme={"legacy"}
              rowData={rows}
              columnDefs={visibleColumnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"

              pagination={true}
              onGridReady={(params) => setGridApi(params.api)}
              onRowDoubleClicked={(e) => openPreviewPage(e.data)}
              overlayNoRowsTemplate="<b>No Records Available</b>"
              paginationPageSize={pageSize}
              paginationPageSizeSelector={[15, 50, 100, 200]}
              suppressPaginationPanel={false}
              
            />
          </div>
        )}
      </section>

      <AESClientWebsiteDrawer
        open={showCustomize}
        setOpen={setShowCustomize}
        title="Customize View Settings"
        size="sm"
      >
        <div style={{ height: 350 }} className="ag-theme-alpine">
          <AgGridReact
            theme={"legacy"}
            rowData={customizeRows}
            columnDefs={customizeColumnDefs}
            rowDragManaged={true}
            animateRows={true}
            suppressMoveWhenRowDragging={true}
            domLayout="autoHeight"
            onRowDragEnd={onCustomizeRowDragEnd}
            defaultColDef={{
              sortable: false,
              filter: false,
              resizable: true,
              
            suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true
            }}
          />
        </div>

        <div style={{ marginTop: 15, textAlign: "right" }}>
          <button
            className="btn btn-default"
            onClick={() => setShowCustomize(false)}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            style={{ marginLeft: 10 }}
            onClick={onSaveCustomize}
          >
            Save
          </button>
        </div>
      </AESClientWebsiteDrawer>
    </div>
  );
};

export default AssetStatements;
